/** SUNTRONIX ONE — CUSTOMER 360° (/customer-360/:id). Single source of truth for a customer. */
import OutstandingPanel from "@/components/finance/outstanding/OutstandingPanel";
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Loader2, ArrowLeft, Building2 } from "lucide-react";
import { OverviewTab } from "./c360/OverviewTab";
import { CommercialTab, OperationsTab, SupportTab } from "./c360/BusinessTabs";
import { DevicesTab, CommunicationTab, TimelineTab } from "./c360/DeviceCommsTabs";
import RecordHistory from "@/components/app/RecordHistory";
import { up } from "@/components/identity/Identity";
import { PeopleTab } from "./c360/PeopleTab";

const TABS = [
  { key: "overview", label: "Overview" }, { key: "people", label: "People" }, { key: "commercial", label: "Commercial" }, { key: "devices", label: "Devices / ITAM" },
  { key: "operations", label: "Operations" }, { key: "support", label: "Support" }, { key: "communication", label: "Communication" }, { key: "outstanding", label: "Outstanding" }, { key: "timeline", label: "Timeline" },
];

export default function Customer360() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [sp, setSp] = useSearchParams();
  const tab = sp.get("tab") || "overview";
  const [head, setHead] = useState(null);
  const [tabData, setTabData] = useState({});
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  const loadHead = useCallback(async () => {
    try { const { data } = await api.get(`/customer-360/${id}`); setHead(data); setErr(null); }
    catch (e) { setErr(e.response?.data?.detail || "Could not load customer"); }
  }, [id]);
  const loadTab = useCallback(async (t, force = false) => {
    if (t === "overview" || t === "people" || (tabData[t] && !force)) return;
    setLoading(true);
    try { const { data } = await api.get(`/customer-360/${id}/${t}`); setTabData((x) => ({ ...x, [t]: data })); }
    catch (e) { toast.error(e.response?.data?.detail || `Could not load ${t}`); }
    finally { setLoading(false); }
  }, [id, tabData]);

  useEffect(() => { loadHead(); }, [loadHead]);
  useEffect(() => { loadTab(tab); }, [tab, loadTab]);
  useEffect(() => { api.post("/global-search/visit", { entity_type: "customer", entity_id: id, title: head?.customer?.account_name || "Customer", route: `/customer-360/${id}` }).catch(() => {}); }, [id, head?.customer?.account_name]);

  const setTab = (t) => { const n = new URLSearchParams(sp); n.set("tab", t); setSp(n); };
  const reload = () => { loadHead(); loadTab(tab, true); };

  if (err) return <div className="py-24 text-center text-sm text-slate-500" data-testid="c360-error">{err}</div>;
  if (!head) return <div className="flex items-center justify-center gap-2 py-24 text-sm text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> Loading customer…</div>;
  const c = head.customer, k = head.counts;
  const chips = [["Quotations", k.quotations, "commercial"], ["Orders", k.orders, "commercial"], ["POs", k.customer_pos, "commercial"], ["Invoices", k.invoices, "commercial"],
    ["Devices", k.devices, "devices"], ["Shipments", k.shipments, "operations"], ["Tickets", k.tickets, "support"], ["Emails", k.emails, "communication"], ["Notes", k.notes, "communication"]];
  const data = tabData[tab];

  return (
    <div className="max-w-[1500px] mx-auto px-3 sm:px-4 py-4 sm:py-5 space-y-4" data-testid="customer-360">
      <button onClick={() => navigate(-1)} className="text-[12px] text-slate-500 hover:text-slate-900 inline-flex items-center gap-1" data-testid="c360-back"><ArrowLeft className="h-3.5 w-3.5" /> Back</button>
      <header className="rounded-xl bg-slate-900 text-white px-5 sm:px-7 py-5 sm:py-6" data-testid="c360-header">
        <div className="flex flex-wrap items-start gap-4">
          <div className="h-12 w-12 rounded-lg bg-white/10 border border-white/10 flex items-center justify-center shrink-0"><Building2 className="h-6 w-6 text-sky-300" /></div>
          <div className="min-w-0">
            <div className="font-mono text-[10px] tracking-[0.2em] text-sky-300 font-semibold uppercase">Customer 360° · {c.customer_code || c.account_no || "—"}</div>
            <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight truncate text-white" data-testid="c360-name">{up(c.account_name)}</h1>
            <div className="text-[12px] text-slate-300 mt-0.5 flex flex-wrap gap-x-3 gap-y-1">
              <span className="font-mono rounded bg-white/10 px-1.5" data-testid="c360-status-chip">{head.status}</span>
              {c.industry && <span>{c.industry}</span>}
              {head.tax.gstin && <span className="font-mono">GSTIN {head.tax.gstin}</span>}
              {head.account_manager && <span>AM · {head.account_manager.name || head.account_manager.email}</span>}
              {head.itam_company && <span>ITAM · {head.itam_company.name}</span>}
            </div>
          </div>
          <div className="ml-auto grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-9 gap-1.5 w-full lg:w-auto" data-testid="c360-counts">
            {chips.map(([l, v, t]) => (
              <button key={l} onClick={() => setTab(t)} title={`${l}: all-time total linked to this customer (account link or exact customer name). Tabs below may list only the most recent records.`} data-testid={`c360-count-${l.toLowerCase()}`} className="rounded-md bg-white/[0.06] hover:bg-white/[0.12] border border-white/10 px-2 py-1.5 text-left">
                <div className="text-[9px] uppercase tracking-wider text-slate-400">{l}</div>{v == null ? <div className="text-[11px] font-semibold leading-tight text-amber-300" title="No ITAM company is linked to this customer, so this figure is unavailable (not zero)." data-testid={`c360-count-${l.toLowerCase()}-unlinked`}>Not linked</div> : <div className="font-heading text-lg font-bold leading-tight">{v}</div>}
              </button>
            ))}
          </div>
        </div>
      </header>

      <nav className="flex gap-1 overflow-x-auto border-b border-slate-200 -mx-3 px-3 sm:mx-0 sm:px-0" data-testid="c360-tabs">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)} data-testid={`c360-tab-${t.key}`}
            className={`px-3 py-2 text-[13px] whitespace-nowrap border-b-2 -mb-px transition-colors ${tab === t.key ? "border-slate-900 text-slate-900 font-semibold" : "border-transparent text-slate-500 hover:text-slate-800"}`}>{t.label}</button>
        ))}
      </nav>

      {tab === "overview" && <OverviewTab data={head} id={id} reload={reload} />}
      {tab === "people" && <PeopleTab id={id} />}
      {tab === "outstanding" && <OutstandingPanel customerName={head.customer.account_name} />}
      {tab !== "overview" && tab !== "people" && tab !== "outstanding" && !data && <div className="flex items-center gap-2 text-sm text-slate-500 py-10 justify-center"><Loader2 className="h-4 w-4 animate-spin" /> Loading {tab}…</div>}
      {tab === "commercial" && data && <CommercialTab data={data} />}
      {tab === "devices" && data && <DevicesTab data={data} />}
      {tab === "operations" && data && <OperationsTab data={data} />}
      {tab === "support" && data && <SupportTab data={data} />}
      {tab === "communication" && data && <CommunicationTab data={data} id={id} reload={reload} />}
      {tab === "timeline" && data && <TimelineTab data={data} />}
      {tab === "timeline" && data && <div className="rounded-lg border border-slate-200 bg-white p-3"><RecordHistory collection="crm_accounts" recordId={id} title="Audit trail — customer record" compact={false} /></div>}
      {loading && data && <div className="text-[11px] text-slate-400">Refreshing…</div>}
    </div>
  );
}
